import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/common/Widgets";
import { Car, Loader2, Sparkles, ShieldCheck, FileBadge } from "lucide-react";
import { fmtNum } from "@/lib/format";

export default function VehicleCollateralSection({ app, reload }) {
  const [form, setForm] = useState({
    vehicle_category: "FOUR_WHEELER", new_or_used: "NEW",
    make: "", model: "", variant: "", manufacturing_year: new Date().getFullYear(),
    color: "", ex_showroom_price: "", on_road_price: "",
    dealer_code: "", dealer_name: "", dealer_address: "",
    chassis_no: "", engine_no: "", registration_no: "", rto: "",
    insurance_company: "", insurance_policy_no: "", insurance_expiry: "", insurance_idv: "",
    hypothecation_in_favor_of: "", hypothecation_marked: false, margin_money_paid: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [valuating, setValuating] = useState(false);
  const [valuationResult, setValuationResult] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/collateral/vehicle/${app.id}`);
      if (r.data) setForm(prev => ({ ...prev, ...r.data }));
    } catch (e) { /* nothing yet */ }
    finally { setLoading(false); }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);

  const lookup = async () => {
    if (!form.make || !form.model) return toast.error("Enter Make + Model first");
    setValuating(true);
    try {
      const r = await api.get(`/collateral/vehicle/valuation/${encodeURIComponent(form.make)}/${encodeURIComponent(form.model)}?year=${form.manufacturing_year}`);
      setValuationResult(r.data);
      // Auto-fill ex_showroom/on_road if empty
      if (!form.ex_showroom_price && r.data?.market_value_inr) set("ex_showroom_price", r.data.market_value_inr);
      if (!form.insurance_idv && r.data?.insurance_idv_inr) set("insurance_idv", r.data.insurance_idv_inr);
      toast.success(`Valuation found — ₹${(r.data.market_value_inr || 0).toLocaleString("en-IN")}`);
    } catch (e) { toast.error("Valuation lookup failed"); }
    finally { setValuating(false); }
  };

  const save = async () => {
    if (!form.make || !form.model || !form.ex_showroom_price || !form.on_road_price) return toast.error("Make, Model, Ex-showroom and On-road price are required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        manufacturing_year: Number(form.manufacturing_year),
        ex_showroom_price: Number(form.ex_showroom_price),
        on_road_price: Number(form.on_road_price),
        insurance_idv: form.insurance_idv ? Number(form.insurance_idv) : null,
        margin_money_paid: form.margin_money_paid ? Number(form.margin_money_paid) : null,
      };
      await api.post(`/collateral/vehicle/${app.id}`, payload);
      toast.success("Vehicle collateral saved");
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <Card className="p-4 space-y-2">{[0,1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</Card>;

  return (
    <div className="space-y-4" data-testid="vehicle-section">
      <SectionHeader
        title="Vehicle Collateral"
        subtitle="Capture vehicle details, dealer info, hypothecation marking and insurance."
        action={<Button onClick={save} disabled={saving} data-testid="vehicle-save-button">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Save Collateral</Button>}
      />
      <Card className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FieldS label="Category"><Select value={form.vehicle_category} onValueChange={v => set("vehicle_category", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="TWO_WHEELER">Two-Wheeler</SelectItem><SelectItem value="FOUR_WHEELER">Four-Wheeler</SelectItem><SelectItem value="COMMERCIAL_VEHICLE">Commercial Vehicle</SelectItem></SelectContent></Select></FieldS>
          <FieldS label="New / Used"><Select value={form.new_or_used} onValueChange={v => set("new_or_used", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="NEW">New</SelectItem><SelectItem value="USED">Used</SelectItem></SelectContent></Select></FieldS>
          <FieldS label="Make *"><Input value={form.make} onChange={e => set("make", e.target.value)} placeholder="Maruti" data-testid="vehicle-make" /></FieldS>
          <FieldS label="Model *"><Input value={form.model} onChange={e => set("model", e.target.value)} placeholder="Swift" data-testid="vehicle-model" /></FieldS>
          <FieldS label="Variant"><Input value={form.variant || ""} onChange={e => set("variant", e.target.value)} placeholder="VXi" /></FieldS>
          <FieldS label="Year"><Input type="number" value={form.manufacturing_year} onChange={e => set("manufacturing_year", e.target.value)} /></FieldS>
          <FieldS label="Color"><Input value={form.color || ""} onChange={e => set("color", e.target.value)} /></FieldS>
          <div className="flex items-end"><Button variant="outline" size="sm" onClick={lookup} disabled={valuating} className="w-full" data-testid="vehicle-lookup-button">{valuating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}Auto Lookup</Button></div>
        </div>
        {valuationResult && (
          <div className="rounded-md border border-accent/30 bg-accent/5 p-3 grid grid-cols-3 gap-3 text-sm">
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Market Value</div><div className="font-semibold tabular">₹ {fmtNum(valuationResult.market_value_inr, 0)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Insurance IDV</div><div className="font-semibold tabular">₹ {fmtNum(valuationResult.insurance_idv_inr, 0)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Loan Eligible</div><div className="font-semibold tabular text-accent">₹ {fmtNum(valuationResult.loan_eligibility_inr, 0)}</div></div>
          </div>
        )}
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="p-4 space-y-3">
          <h3 className="text-sm font-semibold heading flex items-center gap-2"><Car className="h-4 w-4 text-primary" />Pricing</h3>
          <FieldS label="Ex-Showroom Price (₹) *"><Input type="number" value={form.ex_showroom_price} onChange={e => set("ex_showroom_price", e.target.value)} data-testid="vehicle-ex-showroom" /></FieldS>
          <FieldS label="On-Road Price (₹) *"><Input type="number" value={form.on_road_price} onChange={e => set("on_road_price", e.target.value)} data-testid="vehicle-on-road" /></FieldS>
          <FieldS label="Margin Money Paid (₹)"><Input type="number" value={form.margin_money_paid} onChange={e => set("margin_money_paid", e.target.value)} /></FieldS>
        </Card>
        <Card className="p-4 space-y-3">
          <h3 className="text-sm font-semibold heading flex items-center gap-2"><FileBadge className="h-4 w-4 text-accent" />Dealer</h3>
          <FieldS label="Dealer Code"><Input value={form.dealer_code} onChange={e => set("dealer_code", e.target.value)} /></FieldS>
          <FieldS label="Dealer Name"><Input value={form.dealer_name} onChange={e => set("dealer_name", e.target.value)} /></FieldS>
          <FieldS label="Dealer Address"><Input value={form.dealer_address} onChange={e => set("dealer_address", e.target.value)} /></FieldS>
        </Card>
        <Card className="p-4 space-y-3">
          <h3 className="text-sm font-semibold heading flex items-center gap-2"><Car className="h-4 w-4 text-muted-foreground" />Vehicle Identity</h3>
          <FieldS label="Chassis No."><Input value={form.chassis_no} onChange={e => set("chassis_no", e.target.value)} /></FieldS>
          <FieldS label="Engine No."><Input value={form.engine_no} onChange={e => set("engine_no", e.target.value)} /></FieldS>
          {form.new_or_used === "USED" && <FieldS label="Registration No."><Input value={form.registration_no} onChange={e => set("registration_no", e.target.value)} /></FieldS>}
          <FieldS label="RTO"><Input value={form.rto} onChange={e => set("rto", e.target.value)} placeholder="MH-12" /></FieldS>
        </Card>
        <Card className="p-4 space-y-3">
          <h3 className="text-sm font-semibold heading flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-success" />Insurance &amp; Hypothecation</h3>
          <FieldS label="Insurance Company"><Input value={form.insurance_company} onChange={e => set("insurance_company", e.target.value)} /></FieldS>
          <FieldS label="Policy No."><Input value={form.insurance_policy_no} onChange={e => set("insurance_policy_no", e.target.value)} /></FieldS>
          <div className="grid grid-cols-2 gap-2">
            <FieldS label="Policy Expiry"><Input type="date" value={form.insurance_expiry || ""} onChange={e => set("insurance_expiry", e.target.value)} /></FieldS>
            <FieldS label="IDV (₹)"><Input type="number" value={form.insurance_idv} onChange={e => set("insurance_idv", e.target.value)} /></FieldS>
          </div>
          <FieldS label="Hypothecation in favor of"><Input value={form.hypothecation_in_favor_of} onChange={e => set("hypothecation_in_favor_of", e.target.value)} placeholder="Demo Bank" /></FieldS>
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/20 p-2.5"><span className="text-sm">Hypothecation marked on RC</span><Switch checked={form.hypothecation_marked} onCheckedChange={v => set("hypothecation_marked", v)} /></div>
        </Card>
      </div>
    </div>
  );
}

function FieldS({ label, children }) {
  return (<div className="space-y-1"><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>);
}
